import React, { useState, useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormHelperText,
} from '@mui/material';
import {
  CheckCircleRounded as CheckCircleRoundedIcon,
  CircleOutlined as CircleOutlinedIcon,
} from '@mui/icons-material';

import { TOKEN } from '../../../constants';
import { messages } from '../../../constants/Forms/contact.js';
import { emailPattern } from '../../../constants/Forms/index.js';
import { addContacts, findContacts, updateContact, setUserInfo } from '../../../actions/index.js';

import { useLocalStorage } from '../../../CustomHooks/useLocalStorage';

import { getOrSetAnonymousUserId } from '../../../utils/index.js';

import './Contact.css';

export const ContactForm = ({ onFormStateChange }) => {
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();
  const dispatch = useDispatch();
  const email = watch(messages.email, '');
  const subjectSelection = watch(messages.selectSubject, {});
  const [emailExists, setEmailExists] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  const [value] = useLocalStorage(TOKEN);

  const { jwt, user } = useSelector(({ userReducer: { jwt, user } }) => ({
    jwt,
    user,
  }));

  useEffect(() => {
    if (value || jwt) {
      if (!localStorage.getItem(TOKEN)) {
        return;
      }
      const user = atob(localStorage.getItem(TOKEN)?.split('.')?.[1]);

      const parsedUser = JSON.parse(user);

      const localStorageEmail = parsedUser?.email;

      setUserEmail(localStorageEmail);
      setValue(messages.email, localStorageEmail);

      dispatch((dispatch) => setUserInfo(dispatch, parsedUser));
    } else {
      const anonymousUserId = getOrSetAnonymousUserId();

      dispatch((dispatch) => setUserInfo(dispatch, { anonymousUserId }));
    }
  }, [value, jwt]);

  //tracking unsubmitted forms
  const handleInputMixpanelTracking = (e) => {
    const formData = getValues(e.target.name);
    mixpanel.track('Contact Us Form', {
      ...formData,
      path: '/contact',
    });
  };

  // useEffect(() => {
  // const userEmailExists = async () => {
  //   try {
  //     const response = await dispatch(findContacts(email));
  //     const contactFormFromExistingEmail = response.payload.data.contactEmailExists;
  //     const foundContact = contactFormFromExistingEmail.find((item) => item.email === email);

  //     // setValue(messages.firstNameLabel, foundContact?.firstName || '');
  //     // setValue(messages.lastNameLabel, foundContact?.lastName || '');
  //     setValue(messages.phoneLabel, foundContact?.phone || '');
  //     setValue('writeYourMessage', foundContact?.message || '');
  //     setValue(messages.selectSubject, foundContact?.subjectSelection || {});
  //     setUserData(contactFormFromExistingEmail);
  //     setEmailExists(!!foundContact);
  //   } catch (error) {
  //     console.error('Error checking for email');
  //   }
  // };
  // userEmailExists();
  // }, []);

  const handleCheckedBox = (e) => {
    const value = e.target.value;
    const checked = e.target.checked;
    const newSubjects = { ...subjectSelection };
    if (checked) {
      newSubjects[value] = value;
    } else {
      delete newSubjects[value];
    }
    setValue(messages.selectSubject, newSubjects);
  };

  const onSubmit = async (data) => {
    mixpanel.track_forms('Contact Us Form Submitter', {
      ...data,
      path: '/contact',
    });

    try {
      const foundContact = userData?.find((item) => item.email === email);
      if (foundContact) {
        dispatch(updateContact({ id: foundContact.id, contactData: data }));
      } else {
        dispatch(
          addContacts({
            body: {
              ...data,
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              phone: data.phone,
              subjectSelection: data.subjectSelection,
              message: data.message,
              user,
            },
            token: value,
          }),
        );
      }

      onFormStateChange(true);
    } catch (error) {
      console.error('Error during submission:', error);
    }
  };

  return (
    <>
      <form className="contact_form" onSubmit={handleSubmit(onSubmit)}>
        <div className="nameBlock">
          <TextField
            id="firstNameLabel"
            name={messages.firstName}
            placeholder={messages.firstNameLabel}
            fullWidth
            error={!!errors[messages.firstName]}
            autoComplete="off"
            helperText={errors[messages.firstName] ? errors[messages.firstName].message : ''}
            {...register(messages.firstName, {
              required: messages.firstNameRequired,
            })}
            onKeyUp={handleInputMixpanelTracking}
          />
          <TextField
            id="lastNameLabel"
            name={messages.lastName}
            placeholder={messages.lastNameLabel}
            fullWidth
            error={!!errors[messages.lastName]}
            autoComplete="off"
            helperText={errors[messages.lastName] ? errors[messages.lastName].message : ''}
            {...register(messages.lastName, {
              required: messages.lastNameRequired,
            })}
            onKeyUp={handleInputMixpanelTracking}
          />
        </div>
        <div className="emailBlock">
          <TextField
            id="emailLabel"
            name={messages.email}
            placeholder={messages.emailLabel}
            fullWidth
            error={!!errors[messages.email]}
            autoComplete="off"
            helperText={errors[messages.email]?.message || ''}
            {...register(messages.email, {
              required: messages.emailRequired,
              pattern: emailPattern,
            })}
            onKeyUp={handleInputMixpanelTracking}
          />

          <TextField
            id="phoneLabel"
            name={messages.phone}
            placeholder={messages.phoneLabel}
            fullWidth
            error={!!errors[messages.phone]}
            autoComplete="off"
            helperText={errors[messages.phone]?.message || ''}
            {...register(messages.phone, {
              required: messages.phoneNumberRequired,
              pattern: {
                value: /^[0-9]{10}$/,
                message: messages.phoneNumberValid,
              },
            })}
          />
        </div>
        <div className="formControl">
          <FormControl
            component="fieldset"
            name={messages.selectSubject}
            error={!!errors[messages.selectSubject]}
          >
            <h5 className="radioGroupLabel">{messages.selectSubjectLabel}</h5>
            <div id="radioButtonsGroup">
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<CircleOutlinedIcon />}
                    checkedIcon={<CheckCircleRoundedIcon className="checkCircleRoundedIcon" />}
                    value={messages.generalInquiry}
                    onChange={handleCheckedBox}
                    checked={subjectSelection[messages.generalInquiry]}
                    {...register(messages.selectSubject, {
                      required: messages.subjectRequired,
                    })}
                  />
                }
                label={messages.generalInquiry}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    icon={<CircleOutlinedIcon />}
                    checkedIcon={<CheckCircleRoundedIcon className="checkCircleRoundedIcon" />}
                    value={messages.homeLoan}
                    onChange={handleCheckedBox}
                    checked={subjectSelection[messages.homeLoan]}
                    {...register(messages.selectSubject, {
                      required: messages.subjectRequired,
                    })}
                  />
                }
                label={messages.homeLoan}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<CircleOutlinedIcon />}
                    checkedIcon={<CheckCircleRoundedIcon className="checkCircleRoundedIcon" />}
                    value={messages.carLoan}
                    onChange={handleCheckedBox}
                    checked={subjectSelection[messages.carLoan]}
                    {...register(messages.selectSubject, {
                      required: messages.subjectRequired,
                    })}
                  />
                }
                label={messages.carLoan}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<CircleOutlinedIcon />}
                    checkedIcon={<CheckCircleRoundedIcon className="checkCircleRoundedIcon" />}
                    value={messages.others}
                    onChange={handleCheckedBox}
                    checked={subjectSelection[messages.others]}
                    {...register(messages.selectSubject, {
                      required: messages.subjectRequired,
                    })}
                  />
                }
                label={messages.others}
              />

              {errors[messages.selectSubject] && (
                <FormHelperText id="errorMessage">
                  {errors[messages.selectSubject].message}
                </FormHelperText>
              )}
            </div>
          </FormControl>
        </div>

        <TextField
          id="writeYourMessage"
          name="writeYourMessage"
          placeholder={messages.writeYourMessage}
          fullWidth
          multiline
          rows={7}
          error={!!errors.writeYourMessage}
          helperText={errors.writeYourMessage ? errors.writeYourMessage.message : ''}
          {...register('writeYourMessage', { required: messages.enterYourMessage })}
        />
        <div className="contact-us-form-button-wrapper">
          <div className="empty-placeholder-div"></div>
          <div>
            <Button className="contactBtn" type="submit">
              {emailExists ? messages.updateMessage : messages.sendMessage}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};
